import { useMemo, useState, useEffect, useRef, useContext } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { useTheme } from '@mui/material/styles'

import moment from 'moment-timezone'
import _ from 'lodash'
import { useConfirm } from "material-ui-confirm"
import { enqueueSnackbar } from 'notistack'

import {
  MaterialReactTable,
  useMaterialReactTable,
  MRT_ToggleDensePaddingButton,
  MRT_ShowHideColumnsButton,
  MRT_ToggleFullScreenButton,
  MRT_ToggleFiltersButton,
  MRT_ToggleGlobalFilterButton
} from 'material-react-table'

import { MenuItem, Box, IconButton, Typography, Switch, Stack, Button, Tooltip } from '@mui/material'

import SaveAltIcon from '@mui/icons-material/SaveAlt'
import OpenInNewIcon from '@mui/icons-material/OpenInNew'
import AppleIcon from '@mui/icons-material/Apple'
import AndroidIcon from '@mui/icons-material/Android'
import SyncOutlinedIcon from '@mui/icons-material/SyncOutlined'
import EmailOutlinedIcon from '@mui/icons-material/EmailOutlined'
import EditIcon from '@mui/icons-material/Edit'
import QrCode2Icon from '@mui/icons-material/QrCode2'
import InstallMobileOutlinedIcon from '@mui/icons-material/InstallMobileOutlined'
import TrashIcon from '@mui/icons-material/DeleteOutline'

import { AppContext } from '/providers/app-context'
import { getTemplateData } from '/providers/templates-api'
import { getIssuedPasses, deletePasses } from '/providers/passes-api'

import NotifySelectedDialog from './notify-selected-dialog'
import DataExportDialog from "../data-export-dialog"
import ActionErrorDialog from '/components/action-error-dialog'
import useActionErrorDialog from '/components/use-action-error-dialog'
import excelExport from './passes-list-to-excel'
import { createTableStyles } from '../table-styles'
import StyledPaper from '../paper.jsx'

import config from '/providers/config.json'

const PassesTable = () => {
  const theme = useTheme()
  const tableStyles = useMemo(() => createTableStyles(theme), [theme])

  const navigate = useNavigate()
  const confirm = useConfirm()

  const { templateId } = useParams()
  const { apiSource, apiKey, passes, setPasses, setProcessing } = useContext(AppContext)

  const [openNotify, setOpenNotify] = useState(false)
  const [rowSelection, setRowSelection] = useState({})
  const [sorting, setSorting] = useState([])
  const [selectedPasses, setSelectedPasses] = useState([])
  const [rowCount, setRowCount] = useState(0)
  const [loading, setLoading] = useState(false)
  const [template, setTemplate] = useState()
  const [dataExportDialogOpen, setDataExportDialogOpen] = useState(false)
  const { actionError, errorDialogOpen, setErrorDialogOpen, showActionError } = useActionErrorDialog()

  const mode = useRef("live")
  const splitPasses = useRef({ live: [], test: [] })

  const totalAppleInstalls = useMemo(() => _.sum(passes.map(d => d.apple_installs)), [passes])
  const totalGoogleInstalls = useMemo(() => _.sum(passes.map(d => d.google_installs)), [passes])
  const rowVirtualizerInstanceRef = useRef(null)

  const getTemplate = async () => {
    const t = await getTemplateData({ apiSource, apiKey, templateId })
    if (!t.error) setTemplate(t.template)
      // TODO handler error
  }

  const getPasses = async passmode => {
    const startRow = splitPasses.current[passmode].length
    const p = await getIssuedPasses({ apiSource, apiKey, templateId, mode: passmode, startRow, maxRows: config.maxPassesPerRequest })

    const newPasses = [...splitPasses.current[passmode], ...p.passes]

    splitPasses.current[passmode] = newPasses

    if (passmode === mode.current) {
      setRowCount(p.rowCount)
      setPasses(newPasses)

      if (newPasses.length === p.rowCount)
        setLoading(false)
      else
        getPasses(passmode)
    }
  }

  const deletePass = async pass_id => {
    let response = await confirm({ title: "Permanently delete pass", description: "Are you sure you wish to delete this pass? This action is premanent and cannot be undone." })
    if (response.confirmed) processDeletePasses([pass_id])
  }

  const deleteSelectedPasses = async () => {
    let response = await confirm({ title: "Permanently delete passes", description: "Are you sure you wish to delete these passes? This action is premanent and cannot be undone." })
    if (response.confirmed) processDeletePasses(selectedPasses)
  }

  const processDeletePasses = async passes => {
    setProcessing(true)
    enqueueSnackbar({ message: 'Deleting pass' + passes.length > 1 ? "es" : "", variant: 'light' })
    const result = await deletePasses({ apiSource, apiKey, apiMode: mode.current, passes: passes.map(p => p.pass_id) })

    if (result.error) {
      showActionError(result.error, "Error deleting passes")
    } else {
      enqueueSnackbar({ message: "Passes deleted", variant: 'success' })
      const new_pass_list = splitPasses.current[mode.current].filter(p => !passes.map(p => p.pass_id).includes(p.pass_id))
      splitPasses.current[mode.current] = new_pass_list
      setPasses(new_pass_list)
    }
    setProcessing(false)
  }

  useEffect(() => {
    getTemplate()
  }, [templateId])

  useEffect(() => {
    setLoading(true)
    setPasses(splitPasses.current[mode.current])
    getPasses(mode.current)
  }, [templateId, mode.current])

  useEffect(() => {
    const pass_ids = Object.keys(rowSelection)
    const selected_passes = passes.filter(p => pass_ids.includes(p.pass_id))
    setSelectedPasses(selected_passes)
  }, [rowSelection])

  useEffect(() => {
    //scroll to the top of the table when the sorting changes
    try {
      if (passes.length) {
        rowVirtualizerInstanceRef.current?.scrollToIndex?.(0)
      }
    } catch (error) {
      console.error(error)
    }
  }, [sorting])

  const columns = useMemo(() => {

    if (!template) return []

    let cols = [
      {
        accessorKey: 'pass_id',
        header: 'Pass ID',
        filterVariant: 'autocomplete',
        size: 300,
        Footer: () => "Totals",
        Cell: ({ cell, row }) => <div className='link' onClick={() => { navigate(row.id + "/details") }}><OpenInNewIcon className='mr-5 grey' />{cell.getValue()}</div>
      },
      {
        id: 'creation_timestamp',
        accessorFn: (originalRow) => moment(originalRow.creation_timestamp, "YYYYMMDDHHmmss"),
        header: 'Created',
        filterVariant: 'date-range',
        sortingFn: 'datetime',
        size: 200,
        Cell: ({ cell }) => {
          const date = cell.getValue()
          if (date.isValid()) return date.format("DD MMM YYYY HH:mm")
          return "-"
        }
      },
      {
        accessorKey: 'grouping_id',
        header: 'grouping ID',
        filterVariant: 'autocomplete',
        size: 200,
      },
      {
        id: 'active',
        accessorFn: (originalRow) => !originalRow.expiry_date || originalRow.expiry_date > moment().format("YYYYMMDDHHmmss"),
        header: 'Status',
        filterVariant: 'select',
        filterFn: 'equals',
        filterSelectOptions: [
          { label: 'Current', value: "true" },
          { label: 'Expired', value: "false" },
        ],
        size: 200,
        Cell: ({ cell }) => cell.getValue() ? <span className='green'>current</span> : <span className='red'>expired</span>
      },
      {
        id: 'expiry_date',
        accessorFn: (originalRow) => moment(originalRow.expiry_date, "YYYYMMDDHHmmss"),
        header: 'Expiry Date',
        filterVariant: 'date-range',
        sortingFn: 'datetime',
        size: 200,
        Cell: ({ cell }) => {
          const date = cell.getValue()
          if (date.isValid()) return date.format("DD MMM YYYY HH:mm")
          return "-"
        }
      },
      {
        accessorKey: 'apple_installs',
        header: <><AppleIcon /> Apple Device Installs</>,
        filterVariant: 'range',
        filterFn: 'between',
        size: 200,
        Cell: ({ cell }) => numberFormat(cell.getValue()),
        muiTableBodyCellProps: {
          align: 'right',
        },
        muiTableFooterCellProps: {
          align: 'right',
        },
        Footer: () => totalAppleInstalls
      },
      {
        accessorKey: 'google_installs',
        header: <><AndroidIcon /> Google Device Installs</>,
        filterVariant: 'range',
        filterFn: 'between',
        size: 200,
        Cell: ({ cell }) => numberFormat(cell.getValue()),
        muiTableBodyCellProps: {
          align: 'right',
        },
        muiTableFooterCellProps: {
          align: 'right',
        },
        Footer: () => totalGoogleInstalls
      },
      {
        accessorKey: 'update_counter',
        header: 'Number of updates today',
        filterVariant: 'range',
        filterFn: 'between',
        size: 200,
        Cell: ({ cell }) => numberFormat(cell.getValue()),
        muiTableBodyCellProps: {
          align: 'right',
        },
        muiTableFooterCellProps: {
          align: 'right',
        },
        Footer: () => totalGoogleInstalls
      },      
    ]

    const column_types = {
      "scan.disabled": "boolean",
      "pass.validity.end": "date",
      "scan.note": "string",
      "*_valid": "boolean",
      "__*_amount__": "money",
      "__*_balance__": "money",
      "__spend_history__": "omit",
      "__*_points__": "integer",
      "__*_total_points_collected__": "integer",
      "__*_total_points_redeemed__": "integer",
      "__earning_history__": "omit",
      "__spending_history__": "omit",
      "__customer_name__": "string",
      "__security_question__": "string",
      "__security_answer__": "string",
      "__*_total_stamps__": "integer",
      "__*_current_stamps__": "integer",
      "__*_current_full_cards__": "integer",
      "__*_total_redemptions__": "integer",
      "__last_collection__": "date",
      "__last_redemption__": "date",
      "__stamp_history__": "omit",
      "__redeem_history__": "omit",
      "__apple_image__": "omot",
      "__google_image__": "omit"
    }


    if (template.variables && 0) {
      const extra_cols = Object.keys(template.variables)

      extra_cols.map(ec => {

        const type = column_types[ec] ?? "string"
        const header = ec.replace(/\*_/, "")

        switch (type) {
          case "string": {
            cols.push({
              id: ec,
              accessorFn: (originalRow) => originalRow.variables?.[ec],
              header: header.replace(/\*_/, "").replace(/_/g, " "),
              filterVariant: 'autocomplete',
              size: 200,
            })
            break
          }

          case "integer": {
            cols.push({
              id: ec,
              accessorFn: (originalRow) => originalRow.variables?.[ec],
              header: header.replace(/\*_/, "").replace(/_/g, " "),
              size: 200,
              filterVariant: 'range',
              filterFn: 'between',
              Cell: ({ cell }) => numberFormat(cell.getValue()),
              muiTableBodyCellProps: {
                align: 'right',
              },
              muiTableFooterCellProps: {
                align: 'right',
              },
            })
            break
          }

          case "money": {
            cols.push({
              id: ec,
              accessorFn: (originalRow) => originalRow.variables?.[ec],
              header: header.replace(/\*_/, "").replace(/_/g, " "),
              size: 200,
              filterVariant: 'range',
              filterFn: 'between',
              Cell: ({ cell }) => moneyFormat(cell.getValue()),
              muiTableBodyCellProps: {
                align: 'right',
              },
              muiTableFooterCellProps: {
                align: 'right',
              },
            })
            break
          }

          case "boolean": {
            cols.push({
              id: ec,
              accessorFn: (originalRow) => originalRow.variables?.[ec],
              header: header.replace(/\*_/, "").replace(/_/g, " "),
              filterVariant: 'select',
              filterFn: 'equals',
              filterSelectOptions: [
                { label: 'True', value: "true" },
                { label: 'False', value: "false" },
              ],
              size: 200,
              Cell: ({ cell }) => cell.getValue() ? <span className='green'>true</span> : <span className='red'>false</span>
            })
            break
          }

          case "disabled": {
            cols.push({
              id: ec,
              accessorFn: (originalRow) => moment(originalRow.variables?.[ec], "YYYYMMDDHHmmss"),
              header: header.replace(/\*_/, "").replace(/_/g, " "),
              size: 200,
              filterVariant: 'date-range',
              sortingFn: 'datetime',
              Cell: ({ cell }) => {
                const date = cell.getValue()
                if (date.isValid()) return date.format("DD MMM YYYY HH:mm")
                return "-"
              }
            })
            break
          }

          case "date": {
            cols.push({
              id: ec,
              accessorFn: (originalRow) => moment(originalRow.variables?.[ec], "YYYYMMDDHHmmss"),
              header: header.replace(/\*_/, "").replace(/_/g, " "),
              size: 200,
              filterVariant: 'date-range',
              sortingFn: 'datetime',
              Cell: ({ cell }) => {
                const date = cell.getValue()
                if (date.isValid()) return date.format("DD MMM YYYY HH:mm")
                return "-"
              }
            })
            break
          }
        }
      })
    }

    return cols
  }, [template, passes])



  const getRowActions = (row, closeMenu) => {
    let actions = [
      <MenuItem key={1} onClick={() => { navigate("./" + row.id + "/details", { state: { pass: row.original } }) }}><OpenInNewIcon className='mr-2' /> Pass Details</MenuItem>,
      <MenuItem key={2} onClick={() => { navigate("./" + row.id + "/messages", { state: { pass: row.original } }) }}><EmailOutlinedIcon className='mr-2' /> Pass Messages</MenuItem>,
      <MenuItem key={3} onClick={() => { navigate("./" + row.id + "/edit", { state: { pass: row.original } }) }}><EditIcon className='mr-2' /> Pass Editor</MenuItem>,
      <MenuItem key={4} onClick={() => { navigate("./" + row.id + "/install", { state: { pass: row.original } }) }}><InstallMobileOutlinedIcon className='mr-2' /> Pass Install Links</MenuItem>,
      <MenuItem key={5} onClick={() => { deletePass(row.id); closeMenu() }}><TrashIcon className='mr-2 text-red-600' /> <span className='text-red-600'>Delete Pass</span></MenuItem>,
    ]

    if (template?.managed_pass_template) {
      actions.push(<MenuItem key={6} onClick={() => { navigate("./" + row.id + "/scans", { state: { pass: row.original } }) }}><QrCode2Icon className='mr-2' /> View Scans</MenuItem>)
    }

    return actions
  }

  const table = useMaterialReactTable({
    columns,
    data: passes,
    state: {
      isLoading: false,
      sorting,
      rowSelection,
      showProgressBars: loading,
    },
    initialState: { showColumnFilters: false },
    onRowSelectionChange: setRowSelection,
    enablePagination: false,
    enableBottomToolbar: false,
    enableRowSelection: true,
    enableMultiRowSelection: true,
    enableBatchRowSelection: true,
    enableRowActions: true,
    enableStickyHeader: true,
    enableStickyFooter: true,
    enableRowVirtualization: true,
    rowVirtualizerInstanceRef,
    positionToolbarAlertBanner: "none",
    rowCount: rowCount,
    onSortingChange: setSorting,
    getRowId: (originalRow) => originalRow.pass_id,
    ...tableStyles,
    renderRowActionMenuItems: ({ row, closeMenu }) => getRowActions(row, closeMenu),
    renderToolbarInternalActions: ({ table }) => (
      <Box>
        <MRT_ToggleGlobalFilterButton table={table} />
        <MRT_ToggleFiltersButton table={table} />
        <MRT_ShowHideColumnsButton table={table} />
        <MRT_ToggleDensePaddingButton table={table} />
        <MRT_ToggleFullScreenButton table={table} />
        <Tooltip title="Export"><IconButton onClick={() => setDataExportDialogOpen(true)}><SaveAltIcon /></IconButton></Tooltip>
      </Box>
    ),
    renderTopToolbarCustomActions: () => (
      <div className='flex flex-row gap-10'>
        <Stack direction="row" spacing={1} sx={{ alignItems: 'center' }}>
          <Typography>Test Passes</Typography>
          <Switch checked={mode.current === "live"} onChange={e => { mode.current = e.target.checked ? "live" : "test"; setRowSelection({}) }} />
          <Typography>Live Passes</Typography>
        </Stack>

        <Button onClick={refreshData}><SyncOutlinedIcon className='mr-2' />Refresh data</Button>
        <Button onClick={() => setOpenNotify(true)} disabled={selectedPasses.length === 0}><EmailOutlinedIcon className='mr-2' />Notify Selected Passes</Button>
        <Button onClick={() => deleteSelectedPasses()} disabled={selectedPasses.length === 0}><TrashIcon className={selectedPasses.length === 0 ? 'mr-2' : 'mr-2 text-red-400'} /><span className={selectedPasses.length === 0 ? '' : 'text-red-400'}>Delete Selected Passes</span></Button>

      </div>
    )
  })

  const refreshData = () => {
    setLoading(true)
    splitPasses.current[mode.current] = []
    setPasses([])
    getPasses(mode.current)
  }

  return (
    <Box sx={{ height: 'calc(100vh - 185px)', width: '100%',  minHeight: 0, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
      <StyledPaper sx={{ flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column' }} height="100vh">
        <MaterialReactTable table={table} />
        <NotifySelectedDialog open={openNotify} setOpen={setOpenNotify} template={template} passes={selectedPasses} />
        <DataExportDialog open={dataExportDialogOpen} setOpen={setDataExportDialogOpen} fileName={`${template?.id}-passes`} data={splitPasses.current} excelExport={excelExport} template={template} />
        <ActionErrorDialog title="Pass action failed" open={errorDialogOpen} setOpen={setErrorDialogOpen} error={actionError} />
      </StyledPaper>
    </Box>
  )
}

export default PassesTable

const numberFormat = value => <span className={value === 0 ? "grey" : ""}>{value?.toLocaleString()}</span>
const moneyFormat = value => <span className={value === 0 ? "grey" : ""}>{value ? "£" + value.toFixed(2) : "-"}</span>
